import { Platform, useWindowDimensions } from 'react-native';
/**
 * Window width (px/pt) below which web screens switch to their phone layout
 * (single column, no fixed-width side panel). Shared with the public sign-up
 * form (`NARROW_FORM_WIDTH` in intakeForm/ResponsiveFormContext.tsx) so every
 * screen flips at the same width. Purely window-size based; updates on resize
 * and rotation.
 */
export const NARROW_WINDOW_WIDTH = 600;
/** Pure check, exported for tests. */
export const isNarrowWidth = (width) => width < NARROW_WINDOW_WIDTH;
export const useIsNarrowWindow = () => isNarrowWidth(useWindowDimensions().width);
/**
 * Window width at which two-column web layouts (300px side panel + content,
 * e.g. sign-in) have room for the content pane to hold the full desktop
 * banner. Below this, sign-in stays stacked in one column.
 */
export const WIDE_LAYOUT_WIDTH = 900;
/** Pure check, exported for tests. */
export const isWideLayoutWidth = (width) => width >= WIDE_LAYOUT_WIDTH;
export const useIsWideLayout = () => isWideLayoutWidth(useWindowDimensions().width);
/**
 * Phones (web only): the short side of the window is under this. Desktop
 * browsers made narrow are excluded by also requiring a touch (coarse)
 * pointer, so resizing a desktop window never triggers phone behavior.
 */
export const PHONE_SHORT_SIDE = NARROW_WINDOW_WIDTH;
/**
 * Window height below which a landscape phone gets the compact tab header
 * and tab bar (a sideways iPhone is ~390px tall).
 */
export const SHORT_SCREEN_HEIGHT = 500;
/** Pure check, exported for tests. */
export const getPhoneOrientation = (width, height, coarsePointer) => {
    const isLandscape = width > height;
    const isPhone = coarsePointer && Math.min(width, height) < PHONE_SHORT_SIDE;
    return {
        isPhone,
        isLandscape,
        isLandscapePhone: isPhone && isLandscape,
        isPortraitPhone: isPhone && !isLandscape,
        isShortLandscapePhone: isPhone && isLandscape && height < SHORT_SCREEN_HEIGHT,
    };
};
/** True on web when the primary pointer is touch (phones, tablets). */
export const hasCoarsePointer = () => Platform.OS === 'web' &&
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches;
/**
 * Orientation of a web phone; re-renders on rotation (window size changes).
 * Native apps always report `isPhone: false` so their layout is unchanged.
 */
export const usePhoneOrientation = () => {
    const { width, height } = useWindowDimensions();
    return getPhoneOrientation(width, height, hasCoarsePointer());
};
/** Pure check, exported for tests: wider than tall. */
export const isLandscapeSize = (width, height) => width > height;
/**
 * Window is wider than tall, on any platform (updates on rotation). Native
 * Meal Map uses this to switch to the week grid; web phone layouts use
 * usePhoneOrientation instead.
 */
export const useIsLandscape = () => {
    const { width, height } = useWindowDimensions();
    return isLandscapeSize(width, height);
};
